<template>
  <PreviewSheet
    v-motion-fade
    class="standalone-html-preview"
    :class="{ 'standalone-html-preview--no-shadow': !noShadow }"
    :style="{ backgroundColor: builderBackgroundColor }"
  >
    <!-- Error State -->
    <!-- eslint-disable vue/no-bare-strings-in-template -->

    <CenteredIconContent
      v-if="previewError"
      :icon="PhWarningOctagon"
      icon-color="warning"
      title="Unable to load preview"
      subtitle="Please try again."
      class="standalone-html-preview__error"
    >
      <OwnButton
        text="Retry"
        type="button"
        variant="primary"
        @click="retryPreview"
      >
      </OwnButton>
    </CenteredIconContent>

    <!-- Loading State -->
    <div v-else-if="previewLoading" class="preview-loading">
      <OwnProgressSpinner size="24" />
      <OwnType text="Loading preview..." variant="paragraph-medium" />
    </div>
    <!-- eslint-enable vue/no-bare-strings-in-template -->

    <!-- Preview Content -->
    <div v-else-if="resource.htmlPreview?.body">
      <div class="email-template-preview">
        <iframe
          ref="previewIframe"
          sandbox="allow-same-origin allow-popups"
          :style="iframeStyle"
        ></iframe>
      </div>
    </div>

    <!-- Empty State -->
    <!-- eslint-disable vue/no-bare-strings-in-template -->
    <div v-else class="preview-empty">
      <OwnType text="No preview content available" variant="paragraph-medium" />
    </div>
    <!-- eslint-enable vue/no-bare-strings-in-template -->
  </PreviewSheet>
</template>

<script>
import { PhWarningOctagon } from '@phosphor-icons/vue'

import CenteredIconContent from '@/components/status/CenteredIconContent.vue'
import { logError } from '@/logger'
import OwnButton from '@/ui/OwnButton/OwnButton.vue'
import OwnProgressSpinner from '@/ui/OwnProgressSpinner/OwnProgressSpinner.vue'
import OwnType from '@/ui/OwnType/OwnType.vue'

import { PreviewSheet } from '../public'
import { useMergedTheme } from '../public/preview/mixins/useMergedTheme'

export default {
  name: 'StandaloneHTMLPreview',
  components: {
    CenteredIconContent,
    OwnButton,
    OwnProgressSpinner,
    OwnType,
    PreviewSheet,
  },
  mixins: [useMergedTheme],
  props: {
    /**
     * Hide box shadow
     * @public
     */
    noShadow: { type: Boolean, default: false },

    /**
     * Resource Data
     * @public
     */
    resource: { type: Object, required: true },
  },
  data() {
    return {
      PhWarningOctagon, // Available in template as :icon prop
      iframeHeight: 900,
      previewError: null,
      previewLoading: false,
      resizeObserver: null,
    }
  },
  computed: {
    builderBackgroundColor() {
      const { mergedTheme, previewBackgroundColor } = this
      return mergedTheme?.colors.background[previewBackgroundColor]
    },
    formattedPreview() {
      const body = this.resource?.htmlPreview?.body || ''
      if (!body) return ''

      const hasHtmlTag = /<html[\s>]/i.test(body)
      const hasBodyTag = /<body[\s>]/i.test(body)
      let inner = body
      try {
        if (hasHtmlTag) {
          const temp = document.implementation.createHTMLDocument('email')
          temp.documentElement.innerHTML = body
          inner = temp.body?.innerHTML || body
        } else if (hasBodyTag) {
          const m = body.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
          inner = (m && m[1]) || body
        }
      } catch {
        inner = body
      }

      return `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0" />
            <style>
              /* Load Google Fonts for preview */
              /* NOTE: Keep in sync with PreviewTypography.vue and main.scss - updated by scripts/add-google-font.ts */
              @import url('https://fonts.googleapis.com/css2?family=Abril+Fatface&family=Archivo+Narrow:wght@400;500;600;700&family=Cabin:wght@400;500;600;700&family=Cardo:wght@400;700&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Sans:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=Lora:wght@400;500;600;700&family=Manrope:wght@400;500;600;700&family=Merriweather:wght@400;700&family=Montserrat:wght@400;500;600;700&family=Noto+Serif+Display:wght@400;500;600;700&family=Nunito:wght@400;500;600;700&family=Oswald:wght@400;500;600;700&family=Playfair+Display:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Raleway:wght@400;500;600;700&family=Roboto:wght@400;500;700&family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600;8..60,700&family=Bebas+Neue:wght@400;500;600;700&family=Unbounded:wght@400;500;600;700&family=Teko:wght@400;500;600;700&family=BBH+Hegarty:wght@400;500;600;700&family=Michroma:wght@400;500;600;700&family=Pacifico:wght@400;500;600;700&family=Lobster:wght@400;500;600;700&family=Chicle:wght@400;500;600;700&family=Anton:wght@400;500;600;700&family=GFS+Neohellenic:wght@400;500;600;700&family=Cinzel:wght@400;500;600;700&family=Lobster+Two:wght@400;500;600;700&family=Permanent+Marker:wght@400;500;600;700&family=Prata:wght@400;500;600;700&family=Viga:wght@400;500;600;700&family=Chango:wght@400;500;600;700&family=Macondo:wght@400;500;600;700&family=Homemade+Apple:wght@400;500;600;700&family=Lemonada:wght@400;500;600;700&family=Shantell+Sans:wght@400;500;600;700&family=Crushed:wght@400;500;600;700&family=Boogaloo:wght@400;500;600;700&family=Coiny:wght@400;500;600;700&family=Concert+One:wght@400;500;600;700&family=Rajdhani:wght@400;500;600;700&family=Solway:wght@400;500;600;700&family=Graduate:wght@400;500;600;700&family=Alfa+Slab+One:wght@400;500;600;700&family=Jura:wght@400;500;600;700&family=UnifrakturMaguntia&family=Grenze+Gotisch:wght@400;500;600;700&family=Bricolage+Grotesque:wght@400;500;600;700&family=League+Gothic:wght@400;500;600;700&family=Birthstone&display=swap');

              html, body { margin:0; padding:0; background:#ffffff; }
              .canvas {
                margin: 0 auto;
                max-width: 1000px;
                padding: 24px;
                background: #ffffff;
              }
              .canvas * { box-sizing: content-box; }
              img { display:block; }
              body { overflow-x: hidden; }
              table.wrapper_table { width: 100% !important; max-width: 600px !important; }
              table { max-width: 100% !important; }
              td { max-width: 100% !important; }
              td[bgcolor="#FCCF40"] > a {
                width: 100% !important;
                display: block !important;
                text-align: center !important;
              }
            </style>
          </head>
          <body>
            <div class="canvas">
              ${inner}
            </div>
          </body>
        </html>
      `.trim()
    },
    iframeStyle() {
      return {
        border: '0',
        display: 'block',
        height: `${this.iframeHeight || 900}px`,
        margin: '0 auto',
        width: '100%',
      }
    },
  },
  watch: {
    formattedPreview() {
      this.$nextTick(this.addPreviewToIframe)
    },
  },
  mounted() {
    if (this.resource?.htmlPreview?.body)
      this.$nextTick(this.addPreviewToIframe)
  },
  beforeUnmount() {
    this.resizeObserver?.disconnect()
  },
  methods: {
    addPreviewToIframe() {
      try {
        // Check if iframe ref exists (silently return if not ready yet)
        const iframe = this.$refs.previewIframe
        if (!iframe || !this.formattedPreview) {
          return
        }

        // Validate iframe document access
        const doc = iframe.contentDocument || iframe.contentWindow?.document
        if (!doc) {
          throw new Error('Unable to access iframe document')
        }

        // Write content to iframe
        doc.open()
        doc.write(this.formattedPreview)
        doc.close()

        // Setup iframe features
        this.setupIframeAutoResize(doc)
        this.handleIframeLinks(doc)

        // Clear error state
        this.previewError = null
      } catch (err) {
        this.previewError = 'Unable to load preview. Please try again.'
        logError(err, { context: 'StandaloneHTMLPreview.addPreviewToIframe' })
      }
    },
    handleIframeLinks(doc) {
      doc.addEventListener('click', (e) => {
        const link = e.target.closest('a[href]')
        if (!link) return

        // Always prevent default behavior in iframe
        e.preventDefault()
        e.stopPropagation()

        try {
          const { protocol, pathname } = new URL(link.href, location.origin)

          // Block preview/sample links (unsubscribe with preview guest ID)
          const isPreviewLink =
            pathname.includes('/preview-guest-id/') ||
            (pathname.includes('/customers/') &&
              pathname.includes('/unsubscribe'))

          if (isPreviewLink) {
            // No-op: Don't open preview/unsubscribe links
            return
          }

          // Open all http/https links in new tab (brand domain or external)
          if (['http:', 'https:'].includes(protocol)) {
            window.open(link.href, '_blank', 'noopener,noreferrer')
          }
          // For mailto:, tel:, sms:, trigger system handler without navigation
          else if (['mailto:', 'tel:', 'sms:'].includes(protocol)) {
            window.open(link.href, '_self')
          }
        } catch (err) {
          logError(err, {
            context: 'StandaloneHTMLPreview.blockNonDomainLinks',
            href: link.href,
          })
        }
      })
    },
    retryPreview() {
      this.previewError = null
      this.previewLoading = true
      this.$nextTick(this.addPreviewToIframe)
    },
    setupIframeAutoResize(doc) {
      try {
        // Disconnect any existing observer
        this.resizeObserver?.disconnect()

        // Validate target element exists
        const target = doc?.body || doc?.documentElement
        if (!target) {
          logError(
            new Error('No valid target element found for ResizeObserver'),
            { context: 'StandaloneHTMLPreview.setupIframeAutoResize' }
          )
          return
        }

        // Create and attach ResizeObserver
        this.resizeObserver = new ResizeObserver(() => {
          try {
            const body = doc.body
            const html = doc.documentElement
            const h = Math.max(
              body?.scrollHeight || 0,
              body?.offsetHeight || 0,
              html?.clientHeight || 0,
              html?.scrollHeight || 0,
              html?.offsetHeight || 0
            )
            this.iframeHeight = h
          } catch (err) {
            logError(err, {
              context: 'StandaloneHTMLPreview.ResizeObserver callback',
            })
          }
        })

        this.resizeObserver.observe(target)
      } catch (err) {
        logError(err, {
          context: 'StandaloneHTMLPreview.setupIframeAutoResize',
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.standalone-html-preview {
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.25);
  border-radius: 8px;

  &--no-shadow {
    box-shadow: none;
  }
}

.email-template-preview {
  width: 100%;
  max-width: 100%;
  background: var(--white);
}

.preview-loading {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;
  margin: 24px;
  min-height: 300px;
}

.preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  min-height: 300px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
